<template>
  <div v-if="complianceDocument">
    <base-page-heading
      show-back-button
      :title="`${documentType} #${complianceDocument.number}`"
    >
      <template #extra>
        <b-button
          v-if="$can('update', 'ComplianceDocument')"
          variant="primary"
          v-b-modal.compliance-document-edit-modal
          class="mr-2 d-none d-md-inline"
          v-b-tooltip.hover
          title="Edit the Compliance Doc"
        >
          <i class="fa fa-pencil-alt mr-1" /> Edit
        </b-button>

        <b-dropdown
          variant="outline-secondary"
          no-caret
          right
        >
          <template #button-content>
            <i class="fa fa-ellipsis-v" />
          </template>

          <div class="d-md-none">
            <b-dropdown-item
              v-if="$can('update', 'ComplianceDocument')"
              v-b-modal.compliance-document-edit-modal
            >
              <i class="fa fa-pencil-alt mr-1" /> Edit
            </b-dropdown-item>

            <b-dropdown-divider />
          </div>

          <b-dropdown-item
            v-if="$can('create', 'ComplianceDocumentCoverage')"
            v-b-modal.compliance-document-coverage-add-modal
          >
            <i class="fa fa-rotate-right mr-1" />
            {{ terminology.renewCoverageButton }}
          </b-dropdown-item>

          <b-dropdown-item
            v-if="$can('read', 'ComplianceDocument')"
            v-b-modal.compliance-document-coverage-email-modal
          >
            <i class="fa fa-envelope mr-1" />
            Send Email
          </b-dropdown-item>

          <b-dropdown-item
            v-if="$can('update', 'ComplianceDocument')"
            @click="toggleActive"
          >
            <i
              v-if="!complianceDocument.deleted_at"
              class="fa fa-trash mr-1"
            />
            <i
              v-else
              class="fa fa-undo mr-1"
            />
            {{ toggleActiveTooltipText }}
          </b-dropdown-item>

          <b-dropdown-item
            v-if="$can('read', 'Version')"
            :to="{ name: 'versions', query: { model_id: complianceDocument.id, model: 'ComplianceDocument' } }"
          >
            <i class="fa fa-glasses mr-1" />
            Audit Log
          </b-dropdown-item>
        </b-dropdown>
      </template>
    </base-page-heading>

    <div class="content pt-lg-5">
      <b-row class="mb-4">
        <b-col md="6">
          <b-row>
            <b-col
              cols="12"
              sm="6"
            >
              <b-row class="text-muted font-w500 font-size-sm mb-1">
                <b-col>
                  Number
                </b-col>
              </b-row>

              <b-row class="mb-3">
                <b-col>
                  {{ complianceDocument.number }}
                </b-col>
              </b-row>
            </b-col>

            <b-col
              cols="12"
              sm="6"
            >
              <b-row class="text-muted font-w500 font-size-sm mb-1">
                <b-col>
                  {{ terminology.category }}
                </b-col>
              </b-row>

              <b-row class="mb-3">
                <b-col>
                  {{ complianceDocument.category | humanizedFormat | capitalizeFormat }}
                </b-col>
              </b-row>
            </b-col>

            <b-col
              cols="12"
              sm="6"
            >
              <b-row class="text-muted font-w500 font-size-sm mb-1">
                <b-col>
                  User
                </b-col>
              </b-row>

              <b-row class="mb-3">
                <b-col>
                  <b-link :to="{ name: 'user', params: { id: user.id } }">
                    {{ user.name }}
                  </b-link>
                </b-col>
              </b-row>
            </b-col>

            <b-col
              cols="12"
              sm="6"
            >
              <b-row class="text-muted font-w500 font-size-sm mb-1">
                <b-col>
                  State
                </b-col>
              </b-row>

              <b-row class="mb-3">
                <b-col>
                  {{ complianceDocument.state || complianceDocument.user.state || '-' }}
                </b-col>
              </b-row>
            </b-col>
          </b-row>

          <b-row>
            <b-col
              cols="12"
              sm="6"
            >
              <b-row class="text-muted font-w500 font-size-sm mb-1">
                <b-col>
                  {{ terminology.issuerName }}
                </b-col>
              </b-row>

              <b-row class="mb-3">
                <b-col>
                  {{ complianceDocument.issuer_name || '-' }}
                </b-col>
              </b-row>
            </b-col>

            <b-col
              cols="12"
              sm="6"
            >
              <b-row class="text-muted font-w500 font-size-sm mb-1">
                <b-col>
                  {{ terminology.email }}
                </b-col>
              </b-row>

              <b-row class="mb-3">
                <b-col>
                  <b-link
                    v-if="complianceDocument.email"
                    :href="`mailto:${complianceDocument.email}`"
                  >
                    {{ complianceDocument.email }}
                  </b-link>
                  <span v-else>-</span>
                </b-col>
              </b-row>
            </b-col>

            <b-col
              cols="12"
              sm="6"
            >
              <b-row class="text-muted font-w500 font-size-sm mb-1">
                <b-col>
                  {{ terminology.phone }}
                </b-col>
              </b-row>

              <b-row class="mb-3">
                <b-col>
                  <b-link
                    v-if="complianceDocument.phone"
                    :href="`mailto:${complianceDocument.phone}`"
                  >
                    {{ complianceDocument.phone }}
                  </b-link>
                  <span v-else>-</span>
                </b-col>
              </b-row>
            </b-col>
          </b-row>
        </b-col>

        <b-col md="6">
          <hr class="mt-0 mb-3 d-md-none">

          <b-row>
            <b-col
              cols="12"
              sm="6"
            >
              <b-row class="text-muted font-w500 font-size-sm mb-1">
                <b-col>
                  {{ terminology.status }}
                </b-col>
              </b-row>

              <b-row class="mb-3">
                <b-col>
                  <span :class="'text-' + statusVariants[complianceDocument.status]">
                    {{ complianceDocument.status | humanizedFormat | capitalizeFormat }}
                  </span>
                </b-col>
              </b-row>
            </b-col>

            <b-col
              v-if="currentCoverage.start_date"
              cols="12"
              sm="6"
            >
              <b-row class="text-muted font-w500 font-size-sm mb-1">
                <b-col>
                  {{ terminology.period }}
                </b-col>
              </b-row>

              <b-row class="mb-3">
                <b-col>
                  {{ currentCoverage.start_date | dateFormat }} - {{ currentCoverage.end_date | dateFormat }}
                </b-col>
              </b-row>
            </b-col>
          </b-row>

          <b-row>
            <b-col
              cols="12"
              sm="6"
            >
              <b-row class="text-muted font-w500 font-size-sm mb-1">
                <b-col>
                  {{ terminology.verificationStatus }}
                </b-col>
              </b-row>

              <b-row class="mb-3">
                <b-col>
                  <div v-if="currentCoverage.verified">
                    <span class="text-success">
                      Verified
                    </span>
                  </div>
                  <div v-else>
                    <span class="text-secondary">
                      Not Verified
                    </span>
                  </div>
                </b-col>
              </b-row>
            </b-col>

            <b-col
              cols="12"
              sm="6"
            >
              <b-row class="text-muted font-w500 font-size-sm mb-1">
                <b-col>
                  {{ terminology.verificationRequired }}
                </b-col>
              </b-row>

              <b-row class="mb-3">
                <b-col>
                  <span class="text-secondary">
                    {{ complianceDocument.requires_coverage_verification ? "Yes" : "No" }}
                  </span>
                </b-col>
              </b-row>
            </b-col>
          </b-row>

          <hr class="mt-0 mb-3">

          <b-row>
            <b-col>
              <b-row class="mb-3">
                <b-col>
                  Reminders will be sent
                  <span
                    v-if="complianceDocument.reminder_frequency"
                    class="font-weight-bold"
                  >
                    {{ complianceDocument.reminder_frequency_parts | frequencyFormat }}
                  </span>
                  <span
                    v-else
                    class="font-weight-bold"
                  >
                    Only on the day of expiry
                  </span>
                  starting
                  <span class="font-weight-bold">
                    {{ complianceDocument.reminder_days || 0 }} days
                  </span>
                  {{ terminology.expiryReminderText }}
                </b-col>
              </b-row>
            </b-col>
          </b-row>
        </b-col>
      </b-row>

      <b-row>
        <b-col cols="12">
          <b-tabs
            content-class="pt-4 mb-3"
            v-model="activeTab"
          >
            <template #tabs-end>
              <div
                v-show="activeTab === 0"
                class="ml-auto mt-2"
              >
                <b-button
                  v-if="$can('create', 'ComplianceDocumentVerification')"
                  variant="outline-secondary"
                  class="mr-2"
                  v-b-modal.compliance-document-verification-add-modal
                  v-b-tooltip.hover
                  :title="terminology.verificationActionTitle"
                >
                  {{ terminology.verificationActionButton }}
                </b-button>

                <b-button
                  v-if="$can('read', 'ComplianceDocument')"
                  variant="outline-secondary"
                  v-b-modal.compliance-document-verification-email-modal
                  v-b-tooltip.hover
                  :title="terminology.verificationEmailTitle"
                >
                  <i class="fa fa-envelope" />
                </b-button>
              </div>
              <div
                v-show="activeTab === 1"
                class="ml-auto mt-2"
              >
                <b-dropdown
                  class="ml-2"
                  variant="outline-secondary"
                  right
                  v-b-tooltip.hover
                  title="Download spreadsheets"
                >
                  <template #button-content>
                    <b-spinner
                      v-if="downloading"
                      small
                      label="Downloading..."
                    />
                    <i
                      v-else
                      class="fa fa-download mr-1"
                    />
                    Downloads
                  </template>
                  <b-dropdown-item
                    @click="downloadCoveragesSpreadsheet"
                    v-if="$can('read', 'ComplianceDocumentCoverage')"
                  >
                    {{ terminology.previousCoverageTab }}
                  </b-dropdown-item>
                  <b-dropdown-item
                    @click="downloadVerificationsSpreadsheet"
                    v-if="$can('read', 'ComplianceDocumentVerification')"
                  >
                    Verification History
                  </b-dropdown-item>
                </b-dropdown>
              </div>

              <div
                v-show="activeTab === 3"
                class="ml-auto mt-2"
              >
                <b-button
                  variant="outline-secondary"
                  @click="$refs.notesTimeline && $refs.notesTimeline.toggleTimelineForm()"
                >
                  <i class="fa fa-plus mr-1" /> New Note
                </b-button>
              </div>
            </template>
            <b-tab
              active
              :title="terminology.coverageVerificationTab"
            >
              <verifications-table
                :key="verificationsTableKey"
                :columns="verificationsColumns"
                :compliance-document-coverage-id="currentCoverage.id"
                :filters="{ paginate: false }"
                :per-page="1"
                :show-per-page="false"
              />
            </b-tab>
            <b-tab :title="terminology.previousCoverageTab">
              <coverages-table
                :columns="coveragesColumnsForDocumentType"
                :key="coveragesTableKey"
                :compliance-document-id="complianceDocument.id"
              />
            </b-tab>

            <b-tab title="Reminder History">
              <communications-table
                :columns="communicationsColumns"
                :list-provider="$communicationsAPI.getComplianceDocumentsCommunications"
                :reference-id="complianceDocument.id"
              />
            </b-tab>

            <b-tab>
              <template #title>
                <number-badge
                  title="Notes"
                  :total="notesTotal"
                />
              </template>
              <notes-timeline
                ref="notesTimeline"
                :list-func="$notesAPI.getComplianceDocumentsNotes"
                :create-func="$notesAPI.createComplianceDocumentsNote"
                :reference-id="complianceDocument.id"
                reference-type="ComplianceDocument"
                hide-new-note-button
                @total-rows-changed="updateNotesTotal"
              />
            </b-tab>
          </b-tabs>
        </b-col>
      </b-row>
    </div>

    <!-- Modals -->
    <edit-modal
      v-if="$can('update', 'ComplianceDocument')"
      v-model="complianceDocument"
    />
    <new-coverage-modal
      :compliance-document-id="complianceDocument.id"
      :document-type="complianceDocument.document_type"
      @on-save="getComplianceDocument"
    />
    <new-verification-modal
      :compliance-document-coverage-id="currentCoverage.id"
      :document-type="complianceDocument.document_type"
      @on-save="getComplianceDocument"
    />
    <verifications-email-modal
      v-model="complianceDocument"
      :document-type="complianceDocument.document_type"
      :email="user.email"
    />
    <coverages-email-modal
      v-model="complianceDocument"
      :document-type="complianceDocument.document_type"
      :email="user.email"
    />
  </div>
</template>

<script>
import EditModal from '@/components/admin/complianceDocuments/EditModal.vue';
import NewCoverageModal from '@/components/admin/complianceDocumentCoverages/NewModal.vue';
import NewVerificationModal from '@/components/admin/complianceDocumentVerifications/NewModal.vue';
import VerificationsTable from '@/components/admin/complianceDocumentVerifications/Table.vue';
import VerificationsEmailModal from '@/components/admin/complianceDocumentVerifications/EmailModal.vue';
import CoveragesTable from '@/components/admin/complianceDocumentCoverages/Table.vue';
import CoveragesEmailModal from '@/components/admin/complianceDocumentCoverages/EmailModal.vue';
import CommunicationsTable from '@/components/admin/communications/Table.vue';
import NumberBadge from '@/components/shared/NumberBadge.vue';
import NotesTimeline from '@/components/admin/notes/Timeline.vue';
import downloadMixin from '@/mixins/downloadMixin';
import { COMPLIANCE_DOCUMENT_STATUS_VARIANTS } from '@/constants/complianceDocuments';
import complianceDocumentTerminology from '@/utils/complianceDocumentTerminology';
import { COMPLIANCE_DOCUMENT_COMMUNICATION_TABLE_NAME } from '@/constants/communications';
import { COMPLIANCE_DOCUMENT_VERIFICATIONS_TABLE_NAME } from '@/constants/complianceDocumentVerifications';
import { COMPLIANCE_DOCUMENT_COVERAGES_TABLE_NAME } from '@/constants/complianceDocumentCoverages';
import { DateTime } from 'luxon';

export default {
  name: 'ComplianceDocumentShow',
  mixins: [downloadMixin],
  components: {
    EditModal,
    NewCoverageModal,
    NewVerificationModal,
    VerificationsTable,
    CoveragesTable,
    CommunicationsTable,
    NumberBadge,
    CoveragesEmailModal,
    VerificationsEmailModal,
    NotesTimeline,
  },
  data() {
    return {
      communicationsTableName: COMPLIANCE_DOCUMENT_COMMUNICATION_TABLE_NAME,
      verificationsTableName: COMPLIANCE_DOCUMENT_VERIFICATIONS_TABLE_NAME,
      coveragesTableName: COMPLIANCE_DOCUMENT_COVERAGES_TABLE_NAME,
      statusVariants: COMPLIANCE_DOCUMENT_STATUS_VARIANTS,
      complianceDocument: null,
      verificationsTableKey: Date.now(),
      coveragesTableKey: Date.now(),
      reRender: false,
      notesTotal: 0,
      activeTab: 0,
    };
  },
  mounted() {
    this.getComplianceDocument();
  },
  methods: {
    getComplianceDocument() {
      this.$complianceDocumentsAPI
        .get(this.$route.params.id)
        .then((complianceDocument) => (this.complianceDocument = complianceDocument))
        .then(() => this.refreshTables())
        .catch(() => {
          this.$router.push('/404');
        });
    },
    getReminderSchedule() {
      this.$complianceDocumentsAPI
        .getReminderSchedule(this.complianceDocument?.id)
        .then((response) => {
          const { schedule } = response;
          this.schedule = schedule;
        })
        .catch(() => {
          this.schedule = [];
        });
    },
    refreshTables() {
      if (this.reRender) {
        this.refreshVerificationTable();
        this.refreshCoveragesTable();
      } else {
        this.reRender = true;
      }
    },
    refreshVerificationTable() {
      this.verificationsTableKey++;
    },
    refreshCoveragesTable() {
      this.coveragesTableKey++;
    },
    updateNotesTotal(newTotal) {
      this.notesTotal = newTotal;
    },
    toggleActive() {
      this.$swal({
        text: this.toggleActiveConfirmText,
        preConfirm: () => this.$complianceDocumentsAPI.update(this.$route.params.id,  { deleted_at: (!this.complianceDocument.deleted_at ? DateTime.now().toISODate() : null) }),
      }).then((result) => {
        if (result.value) {
          this.complianceDocument = result.value;
        }
        result.timer;
      });
    },
    downloadCoveragesSpreadsheet() {
      this.download(
        this.$complianceDocumentCoveragesAPI.getComplianceDocumentCoveragesSpreadsheet,
        { complianceDocumentId: this.complianceDocument.id },
        'Compliance Document Coverages.csv'
      );
    },
    downloadVerificationsSpreadsheet() {
      this.download(
        this.$complianceDocumentVerificationsAPI.getComplianceDocumentVerificationsSpreadsheet,
        { complianceDocumentCoverageId: this.complianceDocument.current_coverage_id },
        'Compliance Document Verifications.csv'
      );
    },
  },
  computed: {
    user() {
      return this.complianceDocument.user;
    },
    userStatus() {
      return this.statusVariants[this.complianceDocument.status];
    },
    documentType() {
      return this.$options.filters.capitalizeFormat(this.$options.filters.humanizedFormat(this.complianceDocument.document_type));
    },
    terminology() {
      return complianceDocumentTerminology(this.complianceDocument.document_type);
    },
    currentCoverage() {
      return this.complianceDocument.current_coverage;
    },
    communicationsColumns() {
      return this.$store.getters.columnsForTable(this.communicationsTableName);
    },
    coveragesColumns() {
      return this.$store.getters.columnsForTable(this.coveragesTableName);
    },
    coveragesColumnsForDocumentType() {
      return this.coveragesColumns.map((column) => {
        if (column.key === 'start_date') {
          return {
            ...column,
            label: this.terminology.startDateLabel.replace(' *', ''),
          };
        }

        if (column.key === 'end_date') {
          return {
            ...column,
            label: this.terminology.endDateLabel.replace(' *', ''),
          };
        }

        if (column.key === 'actions') {
          return {
            ...column,
            label: this.terminology.verificationHistoryTitle,
          };
        }

        return column;
      });
    },
    verificationsColumns() {
      return this.$store.getters.columnsForTable(this.verificationsTableName);
    },
    toggleActiveTooltipText() {
      return !this.complianceDocument.deleted_at ? 'Deactivate' : 'Reactivate';
    },
    toggleActiveConfirmText() {
      return !this.complianceDocument.deleted_at
        ? 'Are you sure you want to deactivate the Compliance Document?'
        : 'Are you sure you want to reactivate the Compliance Document?';
    },
  },
};
</script>
